import { useState, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { WeavePopNotification } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, NakedButton, Text } from '@frontend/design-system';

type SliderProps = {
  currIndex: number;
  total: number;
  onChange: (i: number) => void;
  queue: WeavePopNotification[];
  onClearAll?: () => void;
  disableClearAll?: boolean;
};

const notificationBubbleStyle = ({ show }: { show: boolean; borderColor?: string; backgroundColor?: string }) => css`
  ::before {
    position: absolute;
    background: ${theme.colors.critical50};
    border-radius: ${theme.borderRadius.large};
    border: 1px solid ${theme.colors.white};
    content: '';
    height: 8px;
    width: 8px;
    right: -5px;
    top: -5px;
    opacity: ${show ? 1 : 0};
    transition: opacity ease-in-out 0.3s;
  }
`;

function useVisitedNotifications(notifications: WeavePopNotification[], currentIndex: number): Set<string> {
  const [visitedIds, setVisitedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const currentNotification = notifications[currentIndex];
    if (currentNotification) {
      setVisitedIds((prev) => {
        const updated = new Set(prev);
        updated.add(currentNotification.id);
        return updated;
      });
    }
  }, [currentIndex, notifications]);

  return visitedIds;
}

function useRedCardVisible(
  notifications: WeavePopNotification[],
  currentIndex: number,
  visitedIds: Set<string>
): boolean {
  return useMemo(() => {
    return notifications.some((notification) => !visitedIds.has(notification.id));
  }, [currentIndex, notifications, visitedIds]);
}

function useOpenNotificationsRedCard(notifications: WeavePopNotification[], isExpanded: boolean): boolean {
  const [seenNotificationIds, setSeenNotificationIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isExpanded) {
      const currentIds = new Set(notifications.map((n) => n.id));
      setSeenNotificationIds((prev) => {
        const updated = new Set(prev);
        currentIds.forEach((id) => updated.add(id));
        return updated;
      });
    }
  }, [isExpanded, notifications]);

  const hasUnseenNotifications = useMemo(() => {
    return notifications.some((notification) => !seenNotificationIds.has(notification.id));
  }, [notifications, seenNotificationIds]);

  return hasUnseenNotifications && !isExpanded;
}

export const Slider = ({ currIndex, total, onChange, queue, onClearAll, disableClearAll }: SliderProps) => {
  const visitedIds = useVisitedNotifications(queue, currIndex);
  const redCardVisible = useRedCardVisible(queue, currIndex, visitedIds);
  const { t } = useTranslation();

  const handleChange = (newIndex: number) => {
    onChange(newIndex);
  };

  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: 40 }}
      exit={{ height: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      css={css`
        overflow: hidden;
        height: 40px;
        border-top-right-radius: ${theme.borderRadius.medium};
        border-top-left-radius: ${theme.borderRadius.medium};
      `}
    >
      <motion.div
        css={css`
          display: flex;
          background: ${theme.colors.neutral10};
          padding: ${theme.spacing(0.5)};
          justify-content: flex-end;
          align-items: center;
          height: 40px;
          min-height: 40px;
          border-bottom: 1px solid ${theme.colors.neutral20};
        `}
      >
        {!!onClearAll && (
          <Button
            variant='secondary'
            css={css`
              margin-right: auto;
              border: none;
              font-weight: ${theme.font.weight.regular};
            `}
            trackingId='notifications-slider-btn-clear-all'
            onClick={onClearAll}
            disabled={disableClearAll}
          >
            {t('Clear All')}
          </Button>
        )}
        <Button
          variant='secondary'
          disabled={currIndex <= 0}
          size='small'
          label={t('Previous')}
          iconName='caret-left-small'
          trackingId='notifications-slider-btn-previous'
          onClick={() => handleChange(currIndex - 1)}
        />
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 ${theme.spacing(1)};
            min-width: ${theme.spacing(6)};
          `}
        >
          <Text
            size='small'
            css={[
              css`
                padding: ${theme.spacing(0, 0.5)};
                background: ${theme.colors.neutral20};
                border-radius: ${theme.borderRadius.small};
                position: relative;
              `,
              notificationBubbleStyle({
                show: redCardVisible,
                backgroundColor: theme.colors.critical50,
              }),
            ]}
          >
            {currIndex + 1}/{total}
          </Text>
        </div>
        <Button
          variant='secondary'
          disabled={currIndex >= total - 1}
          size='small'
          label={t('Next')}
          iconName='caret-right-small'
          trackingId='notifications-slider-btn-next'
          onClick={() => handleChange(currIndex + 1)}
        />
      </motion.div>
    </motion.div>
  );
};

const MotionButton = motion.create(NakedButton);
const MotionText = motion.create(Text);
export const OpenNotifications = ({
  total,
  queue,
  onClearAll,
  disableClearAll,
  isExpanded,
  onExpand,
  position = 'top',
}: Omit<SliderProps, 'currIndex' | 'onChange'> & {
  isExpanded: boolean;
  onExpand: () => void;
  position?: 'top' | 'bottom';
}) => {
  const { t } = useTranslation();
  const redCardVisible = useOpenNotificationsRedCard(queue, isExpanded);

  return (
    <motion.div
      data-testid='new-more-notifications'
      css={[
        css`
          position: sticky;
          top: 0;
          z-index: ${theme.zIndex.highest};
          width: 100%;
        `,
        position === 'bottom' &&
          css`
            bottom: 0;
          `,
      ]}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, height: 0 }}
    >
      <motion.div
        css={css`
          display: flex;
          gap: ${theme.spacing(1)};
          white-space: nowrap;
          margin-top: ${theme.spacing(2)};
          margin: auto;
          width: 100%;
          justify-content: center;
          padding: ${theme.spacing(1)};

          ::before {
            content: '';
            position: absolute;
            top: -${theme.spacing(1)};
            left: 0;
            width: calc(100% + ${theme.spacing(2)});
            height: calc(100% + ${theme.spacing(3)});
            backdrop-filter: blur(8px);
            mask-image: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, black 40%, black 100%);
            z-index: -1;
            margin-left: -${theme.spacing(1)};
            transition: opacity 0.2s ease-in-out;
            pointer-events: none;
          }
        `}
      >
        <MotionButton
          layout
          onClick={onExpand}
          css={css`
            background: ${theme.colors.white};
            display: flex;
            align-items: center;
            padding: ${theme.spacing(1, 1.5)};
            border-radius: ${theme.borderRadius.full};
            box-shadow: ${theme.shadows.floating};
            gap: ${theme.spacing(0.5)};
          `}
          trackingId={!isExpanded ? 'expand-all-notification-pops' : 'collapse-all-notification-pops'}
        >
          {!isExpanded && (
            <MotionText
              as={motion.span}
              data-testid='new-more-notifications-count'
              layout='position'
              size='small'
              weight='bold'
              css={[
                css`
                  padding: ${theme.spacing(0, 0.5)};
                  background: ${theme.colors.neutral5};
                  border-radius: ${theme.borderRadius.small};
                `,
                redCardVisible &&
                  css`
                    background: ${theme.colors.critical50};
                    color: ${theme.colors.white};
                  `,
              ]}
            >
              {total - 1}
            </MotionText>
          )}

          <MotionText
            as={motion.span}
            key={isExpanded ? 'more-notifications' : 'collapse'}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1] }}
            layout='position'
            size='small'
            weight='bold'
          >
            {!isExpanded ? t('More Notifications') : t('Collapse')}
          </MotionText>
          <motion.span layout='position'>
            <Icon name={isExpanded ? 'caret-up-small' : 'caret-down-small'} size={10} />
          </motion.span>
        </MotionButton>
        <MotionButton
          layout
          onClick={() => onClearAll?.()}
          disabled={disableClearAll}
          trackingId='clear-all-notification-pops'
          css={css`
            padding: ${theme.spacing(1, 1.5)};
            border-radius: ${theme.borderRadius.full};
            box-shadow: ${theme.shadows.floating};
            background: ${theme.colors.white};
          `}
        >
          <Text
            size='small'
            css={css`
              margin: 0;
            `}
            weight='bold'
          >
            {t('Clear All')}
          </Text>
        </MotionButton>
      </motion.div>
    </motion.div>
  );
};

export const OpenNotificationsNew = ({
  total,
  queue,
  onClearAll,
  disableClearAll,
  isExpanded,
  onExpand,
  position = 'top',
}: Omit<SliderProps, 'currIndex' | 'onChange'> & {
  isExpanded: boolean;
  onExpand: () => void;
  position?: 'top' | 'bottom';
}) => {
  const { t } = useTranslation();
  const redCardVisible = useOpenNotificationsRedCard(queue, isExpanded);

  return (
    <div
      data-testid='new-more-notifications'
      css={[
        css`
          position: sticky;
          top: 0;
          z-index: ${theme.zIndex.highest};
          width: 100%;
        `,
        position === 'bottom' &&
          css`
            bottom: 0;
          `,
      ]}
    >
      <div
        css={css`
          display: flex;
          gap: ${theme.spacing(1)};
          white-space: nowrap;
          margin-top: ${theme.spacing(2)};
          margin: auto;
          width: 100%;
          justify-content: center;
          padding: ${theme.spacing(1)};

          ::before {
            content: '';
            position: absolute;
            top: -${theme.spacing(1)};
            left: 0;
            width: calc(100% + ${theme.spacing(2)});
            height: calc(100% + ${theme.spacing(3)});
            backdrop-filter: blur(8px);
            mask-image: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, black 40%, black 100%);
            z-index: -1;
            margin-left: -${theme.spacing(1)};
            transition: opacity 0.2s ease-in-out;
            pointer-events: none;
          }
        `}
      >
        {!isExpanded ? (
          <NakedButton
            onClick={onExpand}
            css={css`
              background: ${theme.colors.white};
              display: flex;
              align-items: center;
              padding: ${theme.spacing(1, 1.5)};
              border-radius: ${theme.borderRadius.full};
              box-shadow: ${theme.shadows.floating};
              gap: ${theme.spacing(0.5)};
            `}
            trackingId='expand-all-notification-pops'
          >
            <Text
              data-testid='new-more-notifications-count'
              size='small'
              weight='bold'
              css={[
                css`
                  padding: ${theme.spacing(0, 0.5)};
                  background: ${theme.colors.neutral5};
                  border-radius: ${theme.borderRadius.small};
                  margin: 0;
                `,
                redCardVisible &&
                  css`
                    background: ${theme.colors.critical50};
                    color: ${theme.colors.white};
                  `,
              ]}
            >
              {total - 1}
            </Text>
            <Text
              css={css`
                margin: 0;
              `}
              size='small'
              weight='bold'
            >
              {t('More Notifications')}
            </Text>
            <span>
              <Icon name='caret-down-small' size={10} />
            </span>
          </NakedButton>
        ) : (
          <NakedButton
            onClick={onExpand}
            css={css`
              background: ${theme.colors.white};
              display: flex;
              align-items: center;
              padding: ${theme.spacing(1, 1.5)};
              border-radius: ${theme.borderRadius.full};
              box-shadow: ${theme.shadows.floating};
              gap: ${theme.spacing(0.5)};
            `}
            trackingId='collapse-all-notification-pops'
          >
            <Text
              css={css`
                margin: 0;
              `}
              size='small'
              weight='bold'
            >
              {t('Collapse')}
            </Text>
            <span>
              <Icon name='caret-up-small' size={10} />
            </span>
          </NakedButton>
        )}
        <NakedButton
          onClick={() => onClearAll?.()}
          disabled={disableClearAll}
          trackingId='clear-all-notification-pops'
          css={css`
            padding: ${theme.spacing(1, 1.5)};
            border-radius: ${theme.borderRadius.full};
            box-shadow: ${theme.shadows.floating};
            background: ${theme.colors.white};
          `}
        >
          <Text
            size='small'
            css={css`
              margin: 0;
            `}
            weight='bold'
          >
            {t('Clear All')}
          </Text>
        </NakedButton>
      </div>
    </div>
  );
};
